import * as React from 'react'
import { useParams } from 'react-router-dom';
import { Body } from '../../components/templates/Wrappers';
import { StepPageHeader } from '../../components/molecules/StepPageHeader';
import { getInvestInfoById } from '../../globals/ApiHooks';
import { InvestmentId, InvestmentStep } from '../../shared/Investment';
import { Congratulations, Contract, InvestmentAmount, InvestorTest, KYCData, Terms, Transfer } from '../../components/molecules/InvestementSteps'
import { InvestInfo } from '../../shared/UI';
import { ErrorPage } from '../Error';
import { TranslatedTerms } from '../../globals/TranslatedTerms';
import { lang, translateContent } from '../../globals/Translations';
import { IsMobile, useWindowSize } from '../../globals/UIHooks';
import { BodyWithMenu } from '../../components/organisms/Menu';
import { InvestmentDetails } from '../../components/organisms/InvestmentDetails';
import AnalyticsHooks from '../../globals/AnalyticsHooks';
import { ActivityType } from '../../shared/Activity';

type Step = {
  pathExtention: string,
  index: number
}

type Dictionary<value> = { [key: string]: value }

const StepDictionary: Dictionary<Step> = {
  [InvestmentStep.InvestmentAmountStep]: {
    pathExtention: "amount",
    index: 0
  },
  [InvestmentStep.KYCDataStep]: {
    pathExtention: "information",
    index: 1
  },
  [InvestmentStep.AcceptTerms]: {
    pathExtention: "terms",
    index: 2
  },
  [InvestmentStep.InvestorTest]: {
    pathExtention: "investortest",
    index: 3
  },
  [InvestmentStep.Contract]: {
    pathExtention: "contract",
    index: 4
  },
  [InvestmentStep.Transfer]: {
    pathExtention: "transfer",
    index: 5
  },
  [InvestmentStep.Congratulations]: {
    pathExtention: "completed",
    index: 6
  }
}


const InvestementsStepSmallTitles = [
  lang(InvestmentStep.InvestmentAmountStep),
  lang(InvestmentStep.KYCDataStep),
  lang(InvestmentStep.AcceptTerms),
  lang(InvestmentStep.InvestorTest),
  lang(InvestmentStep.Contract),
  lang(InvestmentStep.Transfer),
  lang(InvestmentStep.Congratulations)
]

export const DetailedInvestmentPage: React.FC = () => {
  let { id } = useParams<{ id: InvestmentId }>();
  const [investInfo, setInvestInfo] = React.useState<InvestInfo>()
  const [loading, setLoading] = React.useState(true)
  const { width } = useWindowSize()


  const getData = async () => {
    setLoading(true)
    const result = await getInvestInfoById(id)
    setInvestInfo(result)
    setLoading(false)
  }

  React.useEffect(() => {
    getData()
  }, [])

  if (!loading && !investInfo) return <ErrorPage />

  const investementStepObject = investInfo && StepDictionary[investInfo.investment.step]
  const investementStep = investInfo && investInfo.investment.step

  const loadable = (task: () => Promise<void>) => async () => {
    setLoading(true)
    await task()
    await getData()
    setLoading(false)
  }

  const DefaultProps = investInfo && {
    asyncTask: loadable, project: investInfo.project, investment: investInfo.investment, investor: investInfo.investor
  }

  React.useEffect(() => {
    if(investInfo && investementStep && investInfo.project.projectId && investInfo.investment.investmentId) {
      (async ()=> {
        await AnalyticsHooks.LogActivityWithoutHook(ActivityType.INVESTMENT_VISIT, {
          step: investementStep ,
          projectId: investInfo.project.projectId,
          investmentId: investInfo.investment.investmentId
        })
      })()
    }
  }, [investInfo, investementStep])


  if (DefaultProps && investementStep === InvestmentStep.InvestmentDetails) return (
    <BodyWithMenu>
      <InvestmentDetails investInfo={investInfo} />
    </BodyWithMenu>
  )

  return (
    <Body loading={loading} style={{ paddingTop: IsMobile(width) ? 130 : 80 }}>
      <StepPageHeader activeStepIndex={loading ? -1 : investementStepObject && investementStepObject.index || 0}
        loading={loading} pathExtention={investementStepObject?.pathExtention}
        title={investInfo && translateContent(investInfo.project.projectTitle) || ''}
        steps={InvestementsStepSmallTitles} id={investInfo?.investment.projectId} />
      {DefaultProps && investementStep === InvestmentStep.InvestmentAmountStep && <InvestmentAmount  {...DefaultProps} />}
      {DefaultProps && investementStep === InvestmentStep.KYCDataStep && <KYCData {...DefaultProps} />}
      {DefaultProps && investementStep === InvestmentStep.AcceptTerms && <Terms {...DefaultProps} />}
      {DefaultProps && investementStep === InvestmentStep.InvestorTest && <InvestorTest {...DefaultProps} />}
      {DefaultProps && investementStep === InvestmentStep.Contract && <Contract {...DefaultProps} />}
      {DefaultProps && investementStep === InvestmentStep.Transfer && <Transfer {...DefaultProps} />}
      {DefaultProps && investementStep === InvestmentStep.Congratulations && <Congratulations {...DefaultProps} />}
    </Body>
  )
};
